<template>
  <div role="radiogroup" :aria-labelledby="id + 'Label'">
    <p v-if="groupLabel" class="u-textBlackAlt u-spaceBottom04" :id="id + 'Label'">
      {{ groupLabel }}
      <span v-if="required" class="u-hiddenVisually">(required)</span><sup class="u-textRed">*</sup>
    </p>
    <ul class="u-spaceItems u-listReset">
      <li v-for="option in options" :key="option.id" class="radio-option">
        <label class="Toggle Toggle--radio">
          <input
            type="radio"
            :name="name"
            :id="option.id"
            :value="option.value"
            :checked="option.selected || currentValue === option.value"
            @change="handleInput"
            :class="`Toggle-input ${required ? 'is-required' : ''}`"
          />
          <!-- Custom-styled radio button appearance -->
          <span class="Toggle-indicator"></span>
          <!-- The actual label text -->
          <span class="Toggle-text">{{ option.label }}</span>
        </label>
      </li>
    </ul>
    <small v-if="helpText" class="u-block u-textShrink2 u-spaceTop02">{{ helpText }}</small>
    <!-- Validation error message (shown when field is invalid) -->
    <p class="u-textShrink1 u-textRed u-spaceTop02" v-if="errorMessage" role="alert">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup>
import { useAttrs, computed, watch, onMounted } from 'vue' // Vue composition API
import { useField } from 'vee-validate' // Form validation

// Get any additional attributes passed to the component
const attrs = useAttrs()

/**
 * Component Props
 * Defines all the configurable options for the TextInput component
 */
const props = defineProps({
  // The current value of the input (used with v-model)
  modelValue: {
    type: String,
    default: '',
  },
  options: {
    type: Array,
    required: true,
    validator: (options) => {
      // Ensure every option has a label, id, and a value.
      // Selected property is optional
      return options.every((opt) => 'label' in opt && 'value' in opt && 'id' in opt)
    },
  },
  // Form field name (defaults to id if not provided)
  name: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  // Visible label text above the input
  groupLabel: {
    type: String,
    default: '',
  },
  // Additional help text shown below the input
  helpText: {
    type: String,
    default: '',
  },
  // Whether the field is required for form submission
  required: {
    type: Boolean,
    default: false,
  },
})

/**
 * Validation Rules Generator
 * Creates vee-validate rules based on the component props
 * Returns an array of validation functions
 */
const validationRules = computed(() => {
  const rules = []

  // Required field validation
  if (props.required) {
    rules.push((value) => {
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        return 'This field is required'
      }
      return true
    })
  }
  return rules
})

/**
 * Find the selected option from the options array
 * Looks for an option with selected: true property
 */
const selectedOption = computed(() => {
  return props.options.find((option) => option.selected === true)
})

/**
 * Get the initial value from selected option or modelValue
 */
const initialValue = computed(() => {
  return props.modelValue || selectedOption.value?.value || ''
})

/**
 * Component Events
 * Events that this component can emit to parent components
 */
const emit = defineEmits(['update:modelValue', 'blur'])

/**
 * Vee-Validate Field Setup
 * Integrates the input with vee-validate for form validation
 */
const {
  errorMessage, // Current validation error message (reactive)
  value: fieldValue, // Internal field value managed by vee-validate
  handleBlur: veeHandleBlur, // Vee-validate's blur handler
  handleChange, // Updates the internal field value
  validate, // Manually trigger validation
} = useField(props.name || props.id, validationRules, {
  initialValue: initialValue.value, // Set initial value from modelValue or selected option
  validateOnValueUpdate: false, // Don't validate on every keystroke
})

/**
 * Input Event Handler
 * Handles user input and updates both vee-validate and parent component
 */
const handleInput = (event) => {
  const newValue = event.target.value
  handleChange(newValue) // Update vee-validate's internal value
  emit('update:modelValue', newValue) // Emit to parent (for v-model)
}

/**
 * Blur Event Handler
 * Triggers validation when user leaves the field
 */
const handleBlur = async (event) => {
  await validate() // Trigger validation on blur
  veeHandleBlur(event) // Call vee-validate's blur handler
  emit('blur', event) // Emit blur event to parent
}

/**
 * Prop-to-Field Synchronization
 * Keeps vee-validate's internal value in sync with the modelValue prop
 * This is important when the parent component updates the value programmatically
 */
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== fieldValue.value) {
      handleChange(newValue)
    }
  },
)

/**
 * Current field value for template binding
 * Uses vee-validate's internal value, falling back to modelValue prop, then selected option
 */
const currentValue = computed(() => {
  return fieldValue.value || props.modelValue || selectedOption.value?.value || ''
})

/**
 * Initialize component with selected option value on mount
 * Emit the initial value if there's a selected option
 */
onMounted(() => {
  const initialSelectedValue = selectedOption.value?.value
  if (initialSelectedValue && !props.modelValue) {
    // Update vee-validate field value
    handleChange(initialSelectedValue)
    // Emit to parent for v-model
    emit('update:modelValue', initialSelectedValue)
  }
})
</script>
